import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { toast } from "material-react-toastify";
import moment from "moment";
import { connect } from "react-redux";
import { handleSpringDataRequest } from "../../api";
import { downloadAccountLedgerInvoice } from "./downloadAccountLedgerInvoice";

/** Spring GST invoice numbers look like SF/YYYY-YY/NNNNN or SF-GST... */
export function isGstSerialInvoiceId(serialInvoiceId) {
  if (serialInvoiceId == null) return false;
  const sid = String(serialInvoiceId).trim();
  if (!sid || sid === "-1") return false;
  const upper = sid.toUpperCase();
  return upper.startsWith("SF/") || upper.startsWith("SF-GST");
}

function canDownloadInvoice(row) {
  if (!isGstSerialInvoiceId(row?.serial_invoice_id)) return false;
  const status = (row?.status || "").toUpperCase();
  if (status === "VOID") return false;
  const type = (row?.type || "").toUpperCase();
  return type === "CREDIT" || type === "DEBIT";
}

/** One row per SF/… serial — prefer Pay Now CREDIT over stamped DEBIT. */
function preferCreditPerSerial(rows) {
  const bySerial = new Map();
  for (const row of rows) {
    if ((row?.status || "").toUpperCase() === "VOID") continue;
    const key = String(row.serial_invoice_id).trim().toUpperCase();
    const existing = bySerial.get(key);
    if (!existing) {
      bySerial.set(key, row);
      continue;
    }
    const existingType = (existing.type || "").toUpperCase();
    const rowType = (row.type || "").toUpperCase();
    if (existingType !== "CREDIT" && rowType === "CREDIT") {
      bySerial.set(key, row);
      continue;
    }
    if (
      row.id > existing.id &&
      !(existingType === "CREDIT" && rowType !== "CREDIT")
    ) {
      bySerial.set(key, row);
    }
  }
  return Array.from(bySerial.values()).sort((a, b) => b.id - a.id);
}

/**
 * India-only GST receipts tab. Client-side filters get-all-invoices by SF/ / SF-GST
 * serial_invoice_id. Reuses existing download-invoice API.
 */
function GstInvoicesDashboard(props) {
  const [groupId, setGroupId] = useState(null);
  const [ledger, setLedger] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);

  const hotelCurr = props?.data?.hotelCurrency || props?.curr || null;
  const currSymbol = hotelCurr === "INR" ? "Rs." : "$";

  useEffect(() => {
    setGroupId(props.groupId);
  }, [props.groupId]);

  const getLedgerData = () => {
    if (!groupId) return;
    setLoading(true);
    handleSpringDataRequest(
      `core/api/v1/account-ledger/get-all-invoices?groupId=${groupId}&hotelId=${groupId}`
    )
      .then((res) => {
        const data = [];
        if (res && res.length > 0) {
          res.forEach((val) => {
            if (isGstSerialInvoiceId(val?.serial_invoice_id)) {
              data.push(val);
            }
          });
        }
        setLedger(preferCreditPerSerial(data));
      })
      .catch((err) => {
        console.log("GST invoices fetch err: ", err);
        toast.error(err?.message || "Failed to load GST invoices");
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (groupId) getLedgerData();
  }, [groupId]);

  const handleDownload = async (row) => {
    try {
      setDownloadingId(row.id);
      await downloadAccountLedgerInvoice(groupId, row.id, props.data?.hotelId);
    } catch (err) {
      console.log("GST invoice download err: ", err);
      toast.error("Failed to download GST invoice");
    } finally {
      setDownloadingId(null);
    }
  };

  const formatAmount = (amount) => {
    const value = typeof amount === "number" ? amount : parseFloat(amount);
    const formatted = Number.isNaN(value) ? "0.00" : value.toFixed(2);
    return `${currSymbol} ${formatted}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const m = moment(dateStr);
    return m.isValid() ? m.format("DD MMM YYYY, hh:mm A") : dateStr;
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Typography
        variant="h6"
        style={{ fontWeight: 500, marginBottom: "0.5rem" }}
      >
        GST Invoices
      </Typography>
      <Typography
        variant="body2"
        color="textSecondary"
        style={{ marginBottom: "1rem" }}
      >
        GST receipts generated after Pay Now / Razorpay payments (SF/… invoice
        numbers)
      </Typography>

      {loading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress size={32} />
        </Box>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>GST Invoice No.</TableCell>
                <TableCell>Transaction date</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Download Invoice</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {ledger.length > 0 ? (
                ledger.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell>
                      <strong>{row.serial_invoice_id || "-"}</strong>
                    </TableCell>
                    <TableCell>{formatDate(row.date)}</TableCell>
                    <TableCell>{row.description || "-"}</TableCell>
                    <TableCell>{formatAmount(row.amount)}</TableCell>
                    <TableCell>
                      {canDownloadInvoice(row) ? (
                        <Button
                          variant="custom-button"
                          style={{ height: "2rem" }}
                          disabled={downloadingId === row.id}
                          onClick={() => handleDownload(row)}
                        >
                          {downloadingId === row.id ? (
                            <CircularProgress size={16} />
                          ) : (
                            "Download"
                          )}
                        </Button>
                      ) : (
                        "-"
                      )}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    No GST invoices yet
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}

function mapStateToProps(state) {
  return { data: state.auth?.hotels?.[0] };
}

export default connect(mapStateToProps)(GstInvoicesDashboard);
